import React, { useRef } from 'react';

export interface SegmentedOption<T extends string = string> {
  value: T;
  label: string;
  shortLabel?: string;
  countTone?: 'warning';
  count?: number | string;
  icon?: React.ReactNode;
  testId?: string;
}

export interface SegmentedProps<T extends string = string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  fullWidth?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  'data-testid'?: string;
}

export function Segmented<T extends string = string>({
  options,
  value,
  onChange,
  fullWidth = false,
  size = 'md',
  className = '',
  'data-testid': dataTestId
}: SegmentedProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);

  const sizeClasses = {
    sm: 'h-7 max-md:h-11 max-md:min-w-11 px-2.5 text-xs',
    md: 'h-8 max-lg:h-9 max-md:h-11 max-md:min-w-11 px-3.5 text-[13px]',
    lg: 'h-9 max-lg:h-10 max-md:h-11 max-md:min-w-11 px-4 text-sm'
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = options.findIndex((opt) => opt.value === value);
    if (currentIndex === -1) return;

    const isRTL =
      typeof document !== 'undefined'
        ? document.dir === 'rtl' || document.documentElement.dir === 'rtl'
        : true;

    let nextIndex = currentIndex;

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      // In RTL, ArrowRight visually moves to the right (previous in DOM order)
      nextIndex = isRTL
        ? (currentIndex - 1 + options.length) % options.length
        : (currentIndex + 1) % options.length;
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      // In RTL, ArrowLeft visually moves to the left (next in DOM order)
      nextIndex = isRTL
        ? (currentIndex + 1) % options.length
        : (currentIndex - 1 + options.length) % options.length;
    } else if (e.key === 'Home') {
      e.preventDefault();
      nextIndex = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      nextIndex = options.length - 1;
    }

    if (nextIndex !== currentIndex) {
      const nextOption = options[nextIndex];
      if (nextOption) {
        onChange(nextOption.value);
        const buttons = containerRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
        buttons?.[nextIndex]?.focus();
      }
    }
  };

  return (
    <div
      ref={containerRef}
      data-testid={dataTestId}
      role="tablist"
      aria-orientation="horizontal"
      onKeyDown={handleKeyDown}
      className={`inline-flex items-center gap-1 p-1 bg-base-200 border border-base-300 rounded-xl select-none ${
        fullWidth ? 'w-full grid' : ''
      } ${className}`}
      style={
        fullWidth
          ? { gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }
          : undefined
      }
    >
      {options.map((option) => {
        const isSelected = option.value === value;
        return (
          <button
            key={option.value}
            role="tab"
            data-testid={option.testId}
            type="button"
            tabIndex={isSelected ? 0 : -1}
            aria-selected={isSelected}
            onClick={() => onChange(option.value)}
            className={`inline-flex items-center justify-center gap-1.5 rounded-[9px] font-semibold transition active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 ring-primary ${
              sizeClasses[size]
            } ${
              isSelected
                ? 'bg-base-300 text-base-content shadow-sm'
                : 'text-base-content/60 hover:text-base-content hover:bg-base-200/60'
            }`}
          >
            {option.icon}
            {option.shortLabel ? (
              <>
                <span className="lg:hidden">{option.shortLabel}</span>
                <span className="hidden lg:inline">{option.label}</span>
              </>
            ) : (
              <span>{option.label}</span>
            )}
            {option.count !== undefined && (
              <span
                className={`inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-base-100/60 text-[11px] leading-none ${
                  option.countTone === 'warning' ? 'text-warning-soft' : 'text-base-content/60'
                }`}
              >
                {option.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

Segmented.displayName = 'Segmented';
